import type { Item, MembrePublic } from '@camellie/shared';
import { libelleRecurrence } from '@camellie/shared';
import { aUneHeure, heure, jourCourt } from '../lib/format';

interface Props {
  item: Item;
  membres: Map<string, MembrePublic>;
  /** Membre connecte : ce qui lui est assigne porte un liseré. */
  moi: string;
  /** Absent pour un rendez-vous, qui ne se coche pas. */
  onBasculer?: (id: string) => void;
  /** Ouvre la fiche d'edition. */
  onOuvrir?: (id: string) => void;
  /** Affiche la date plutot que l'heure (pour les elements en retard). */
  montrerDate?: boolean;
}

function Crochet() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <polyline points="4 12.5 9.5 18 20 6.5" />
    </svg>
  );
}

/**
 * Une ligne de liste, decoupee en deux zones tactiles distinctes.
 *
 *   [  case  ][ titre, heure, lieu, assignation           > ]
 *     cocher              ouvrir la fiche
 *
 * Le decoupage est identique sur tous les ecrans. Une ligne d'apparence
 * semblable qui reagirait differemment selon l'onglet ferait mal viser — et
 * c'est precisement le matin qu'on vise mal.
 *
 * La case dessinee fait 26 px mais sa zone tactile en fait 56 : on coche au
 * pouce, parfois en marchant. Le chevron n'est pas une troisieme cible, il
 * appartient a la zone « details » et se contente d'annoncer qu'il y a
 * quelque chose a ouvrir.
 *
 * Deux boutons cote a cote plutot qu'un bouton dans un bouton : ce dernier
 * n'est pas du HTML valide, et le clic serait ambigu.
 */
export function LigneItem({ item, membres, moi, onBasculer, onOuvrir, montrerDate }: Props) {
  const fait = item.statut === 'fait';
  const assigne = item.assigne_a ? membres.get(item.assigne_a) : undefined;
  const mienne = item.assigne_a === moi;
  const provisoire = item.id.startsWith('tmp_');

  const cochable = onBasculer !== undefined && !provisoire;
  const ouvrable = onOuvrir !== undefined && !provisoire;

  const classes = [
    'ligne',
    fait ? 'faite' : '',
    mienne ? 'mienne' : '',
    provisoire ? 'en-attente' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const details = (
    <>
      {item.type === 'rdv' && item.du_le && aUneHeure(item.du_le) && (
        <span className="heure">{heure(item.du_le)}</span>
      )}

      <span className="corps">
        <span className="titre">{item.titre}</span>
        {(assigne || montrerDate || lieuDe(item) || item.recurrence) && (
          <span className="meta">
            {assigne && (
              <span className="puce-membre">
                <span className="point-membre" style={{ background: assigne.couleur }} />
                {assigne.prenom}
              </span>
            )}
            {lieuDe(item) && <span>{lieuDe(item)}</span>}
            {montrerDate && item.du_le && <span>{jourCourt(item.du_le)}</span>}
            {item.recurrence && (
              <span className="repetee">↻ {libelleRecurrence(item.recurrence)}</span>
            )}
          </span>
        )}
      </span>

      {ouvrable && (
        <span className="chevron" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <polyline points="9 5 16 12 9 19" />
          </svg>
        </span>
      )}
    </>
  );

  return (
    <div className={classes}>
      {cochable && (
        <button
          type="button"
          className={'case-zone' + (fait ? ' cochee' : '')}
          onClick={() => onBasculer(item.id)}
          aria-pressed={fait}
          aria-label={(fait ? 'Decocher ' : 'Cocher ') + item.titre}
        >
          <span className="boite" aria-hidden="true">
            <Crochet />
          </span>
        </button>
      )}

      {ouvrable ? (
        <button
          type="button"
          className="ligne-action"
          onClick={() => onOuvrir(item.id)}
          aria-label={'Ouvrir ' + item.titre}
        >
          {details}
        </button>
      ) : (
        <div className="ligne-action">{details}</div>
      )}
    </div>
  );
}

/** Le lieu vit dans le payload : aucune colonne dediee, aucune migration. */
function lieuDe(item: Item): string | null {
  const p = item.payload as { lieu?: unknown };
  return typeof p?.lieu === 'string' && p.lieu.trim() !== '' ? p.lieu : null;
}
